import { FC, MouseEventHandler, memo } from 'react';
import {
  StyledCalendar,
  StyledCalendarDate,
  StyledCalendarHeader,
  StyledCalendarHeaderControl,
  StyledCalendarHeaderControls,
  StyledCalendarWeekdays,
} from './Calendar.styles';
import { CalendarDay } from '@versita/shared/models';
import { ChevronRight, ChevronLeft } from '../../icons';
import { Info } from 'luxon';

export interface CalendarProps {
  /** the heading for the calendar e.g. Apr 2023 */
  heading: string;
  /** the days to show in the calendar */
  days: CalendarDay[];
  /** the currently selected day formatted according to SELECTED_DAY_FORMAT */
  selectedDay?: string | null;
  /** callback for when the month should be incremented */
  onIncrementMonth: MouseEventHandler;
  /** callback for when the month should be decremented */
  onDecrementMonth: MouseEventHandler;
  /** callback for when the day should be set to today */
  onSelectToday: MouseEventHandler;
  /** callback for when a day is selected */
  onDaySelect?: (day: CalendarDay) => void;
}

/**
 * Simple calendar component for showing the days in the month and
 * highlighting the current day if applicable.
 *
 * The days can be generated using the `getCalendarDays` helper function
 * which is compatible with the format expected by this component
 */
export const Calendar: FC<CalendarProps> = ({
  heading,
  onIncrementMonth,
  onDecrementMonth,
  days,
  onDaySelect,
  onSelectToday,
  selectedDay,
}) => {
  const weekdays = Info.weekdaysFormat('short', { locale: 'en-GB' });
  return (
    <>
      <StyledCalendarHeader>
        <h2>{heading}</h2>
        <StyledCalendarHeaderControls>
          <StyledCalendarHeaderControl
            onClick={onDecrementMonth}
            title="View previous month"
          >
            <ChevronLeft />
          </StyledCalendarHeaderControl>
          <StyledCalendarHeaderControl
            onClick={onSelectToday}
            title="Go to today"
            style={{ verticalAlign: 'top' }}
          >
            &#x2981;
          </StyledCalendarHeaderControl>
          <StyledCalendarHeaderControl
            onClick={onIncrementMonth}
            title="View next month"
          >
            <ChevronRight />
          </StyledCalendarHeaderControl>
        </StyledCalendarHeaderControls>
      </StyledCalendarHeader>
      <StyledCalendar>
        {weekdays.map((weekday) => (
          <StyledCalendarWeekdays key={weekday}>
            {weekday}
          </StyledCalendarWeekdays>
        ))}
        {days.map((day, i) => (
          <StyledCalendarDate
            key={i}
            tabIndex={day.outsideCurrentMonth ? -1 : 0}
            onClick={
              !day.outsideCurrentMonth ? () => onDaySelect?.(day) : undefined
            }
            $selected={!!selectedDay && selectedDay === day.date}
            data-selected={!!selectedDay && selectedDay === day.date}
            $today={day.current}
            $dim={day.outsideCurrentMonth}
            data-testid="calendar-date"
          >
            {day.label}
          </StyledCalendarDate>
        ))}
      </StyledCalendar>
    </>
  );
};

export default memo(Calendar);
